import { useState, useEffect, useCallback, useRef, useContext } from "react";
import ReactDOM from "react-dom";
import { C, THUMB_BASE, LOGO_BASE } from "./utils";
import { ThemeCtx } from "./AppShell";
import ProductProfileDetail from "./ProductProfileDetail";
import { CaptureContent } from "./CaptureScreen";
const API_BASE = "https://cip.medsphere.net/api";

// ─── SVG Icons (same as Dashboard) ───────────────────────────────────────────
const PPIcon = ({ name, size = 13, color = "currentColor" }) => {
  const s = { width: size, height: size, flexShrink: 0, display: "inline-block" };
  if (name === "attach") return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>;
  if (name === "like")   return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z"/><path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/></svg>;
  if (name === "chat")   return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>;
  if (name === "star")   return <svg style={s} viewBox="0 0 24 24" fill="#EF9F27"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>;
  return null;
};

// ─── Constants ────────────────────────────────
const PAGE_SIZE    = 20;
const API_ENDPOINT = "https://cip.medsphere.net/api/product_profile_list";

const isImage  = (p = "") => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p);
const openDocTab = (docId, subId) => {
  const base = window.location.origin + window.location.pathname;
  window.open(`${base}?doc=${docId}&dt=97&sub=${subId||""}`, "_blank", "noopener,noreferrer");
};
const fileName = (p = "") => p.split("/").pop();

// ─── Priority config ──────────────────────────
const PRIORITY_CONFIG = {
  HIGH:   { color: "#DC2626", bg: "#FEF2F2", label: "HIGH",   bar: "#EF4444" },
  MEDIUM: { color: "#D97706", bg: "#FFFBEB", label: "MED",    bar: "#F59E0B" },
  LOW:    { color: "#6B7280", bg: "#F9FAFB", label: "LOW",    bar: "#9CA3AF" },
  null:   { color: "#6B7280", bg: "#F9FAFB", label: "—",      bar: "#E5E7EB" },
};

// ─── Phase config ─────────────────────────────
const PHASE_CONFIG = {
  "Approved":  { color: "#059669", bg: "#D1FAE5" },
  "Phase III": { color: "#1D4ED8", bg: "#DBEAFE" },
  "Phase II":  { color: "#7C3AED", bg: "#EDE9FE" },
  "Phase I":   { color: "#0891B2", bg: "#CFFAFE" },
};

// ─── Data fetch hook ──────────────────────────
function useProductLoader({ user, subproject }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [hasMore, setHasMore]   = useState(true);
  const [error, setError]       = useState(null);
  const [search, setSearch]     = useState("");
  const [searchInput, setSearchInput] = useState("");
  const pageRef     = useRef(0);
  const searchTimer = useRef(null);

  const load = useCallback(async (reset = false, overrides = {}) => {
    if (!subproject) return;
    setLoading(true); setError(null);
    const first = reset ? 0 : pageRef.current * PAGE_SIZE;
    const params = new URLSearchParams({
      subprojectid: subproject.SUBPROJECTID,
      userid:       user.USERID,
      first,
      last:         PAGE_SIZE,
      search:       overrides.search ?? search,
    });
    try {
      const res  = await fetch(`${API_ENDPOINT}?${params}`, {
        headers: { "Auth-Code": user.AUTH_CODE }
      });
      const r    = await res.json();
      if (r.STATUS === "1" && Array.isArray(r.DATA) && r.DATA.length > 0) {
        setProducts(prev => reset ? r.DATA : [...prev, ...r.DATA]);
        setHasMore(r.DATA.length === PAGE_SIZE);
        pageRef.current = reset ? 1 : pageRef.current + 1;
      } else {
        if (reset) setProducts([]);
        setHasMore(false);
      }
    } catch { setError("Failed to load products."); }
    setLoading(false);
  }, [subproject, user, search]);

  useEffect(() => {
    pageRef.current = 0; setProducts([]); setHasMore(true);
    load(true);
  }, [subproject?.SUBPROJECTID]);

  const handleSearch = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val); pageRef.current = 0; setProducts([]); setHasMore(true);
      load(true, { search: val });
    }, 500);
  };

  return { products, loading, hasMore, error, searchInput, handleSearch, loadMore: () => load(false) };
}

// ─── Load ALL products for chart view (no pagination) ────────────────────────
function useAllProductsLoader({ user, subproject }) {
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  useEffect(() => {
    if (!subproject || !user) return;
    setLoading(true); setError(null);
    const params = new URLSearchParams({
      subprojectid: subproject.SUBPROJECTID,
      userid:       user.USERID,
    });
    fetch(`${API_ENDPOINT}?${params}`, { headers: { "Auth-Code": user.AUTH_CODE } })
      .then(r => r.json())
      .then(r => setProducts(r.STATUS==="1"&&Array.isArray(r.DATA)?r.DATA:[]))
      .catch(() => setError("Failed to load products."))
      .finally(() => setLoading(false));
  }, [subproject?.SUBPROJECTID]);
  return { products, loading, error };
}

// ─── Phase pill ───────────────────────────────
function PhasePill({ phase }) {
  if (!phase) return null;
  const cfg = PHASE_CONFIG[phase] || { color: "#6B7280", bg: "#F3F4F6" };
  return (
    <span style={{ fontSize: "10px", fontWeight: "600", color: cfg.color, background: cfg.bg, borderRadius: "4px", padding: "2px 7px", letterSpacing: "0.3px", whiteSpace: "nowrap" }}>
      {phase}
    </span>
  );
}

// ─── Country badge ────────────────────────────
function CountryBadge({ country }) {
  if (!country) return null;
  return (
    <span style={{ fontSize: "10px", fontWeight: "500", color: "#374151", background: "#F3F4F6", borderRadius: "4px", padding: "1px 6px", border: "1px solid #E5E7EB" }}>
      {country}
    </span>
  );
}

// ─── Delivery badge ───────────────────────────
function DeliveryBadge({ delivery }) {
  if (!delivery) return null;
  return (
    <span style={{ fontSize: "10px", fontWeight: "600", color: "#0369A1", background: "#E0F2FE", borderRadius: "4px", padding: "1px 7px" }}>
      {delivery}
    </span>
  );
}

// ─── Smart tooltip position (stays within viewport) ──
function smartPos(x, y, tipW=340, tipH=220) {
  const vw = window.innerWidth, vh = window.innerHeight;
  let left = x + 18, top = y - 14;
  if (left + tipW > vw - 8) left = x - tipW - 10;
  if (left < 8)              left = 8;
  if (top + tipH > vh - 8)  top  = vh - tipH - 8;
  if (top < 8)               top  = 8;
  return { left, top };
}

// ─── Circular SVG flag icon ───────────────────
function FlagIcon({ country, size = 16 }) {
  const c = (country || "").trim();
  // US
  if (c === "US" || c === "USA" || c === "United States") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ borderRadius: "50%", flexShrink: 0 }}>
      <clipPath id="uscirc"><circle cx="16" cy="16" r="16"/></clipPath>
      <g clipPath="url(#uscirc)">
        <rect width="32" height="32" fill="#B22234"/>
        {[0,1,2,3,4,5].map(i=><rect key={i} y={i*4.9+2.45} width="32" height="2.45" fill="#fff"/>)}
        <rect width="13" height="15" fill="#3C3B6E"/>
        {[[2,2],[5,2],[8,2],[11,2],[3.5,4.5],[6.5,4.5],[9.5,4.5],[2,7],[5,7],[8,7],[11,7],[3.5,9.5],[6.5,9.5],[9.5,9.5],[2,12],[5,12],[8,12],[11,12]].map(([x,y],i)=>(
          <circle key={i} cx={x} cy={y} r="0.9" fill="#fff"/>
        ))}
      </g>
    </svg>
  );
  // EU / Europe
  if (c === "EU" || c === "Europe" || c === "European Union") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ borderRadius: "50%", flexShrink: 0 }}>
      <clipPath id="eucirc"><circle cx="16" cy="16" r="16"/></clipPath>
      <g clipPath="url(#eucirc)">
        <rect width="32" height="32" fill="#003399"/>
        {Array.from({length:12},(_,i)=>{
          const a = (i/12)*2*Math.PI - Math.PI/2;
          return <text key={i} x={16+10*Math.cos(a)-3} y={16+10*Math.sin(a)+4} fontSize="6" fill="#FFDD00">★</text>;
        })}
      </g>
    </svg>
  );
  // Global / International
  if (c === "Global" || c === "International" || c === "Worldwide") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ borderRadius: "50%", flexShrink: 0 }}>
      <circle cx="16" cy="16" r="16" fill="#E0F2FE"/>
      <circle cx="16" cy="16" r="16" fill="none" stroke="#0891B2" strokeWidth="1"/>
      <ellipse cx="16" cy="16" rx="7" ry="16" fill="none" stroke="#0891B2" strokeWidth="1"/>
      <line x1="0" y1="16" x2="32" y2="16" stroke="#0891B2" strokeWidth="1"/>
      <line x1="16" y1="0" x2="16" y2="32" stroke="#0891B2" strokeWidth="1"/>
    </svg>
  );
  // UK
  if (c === "UK" || c === "United Kingdom") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ borderRadius: "50%", flexShrink: 0 }}>
      <clipPath id="ukcirc"><circle cx="16" cy="16" r="16"/></clipPath>
      <g clipPath="url(#ukcirc)">
        <rect width="32" height="32" fill="#012169"/>
        <path d="M0,0 L32,32 M32,0 L0,32" stroke="#fff" strokeWidth="6"/>
        <path d="M0,0 L32,32 M32,0 L0,32" stroke="#C8102E" strokeWidth="3"/>
        <path d="M16,0 L16,32 M0,16 L32,16" stroke="#fff" strokeWidth="8"/>
        <path d="M16,0 L16,32 M0,16 L32,16" stroke="#C8102E" strokeWidth="5"/>
      </g>
    </svg>
  );
  // Japan
  if (c === "Japan" || c === "JP") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ borderRadius: "50%", flexShrink: 0 }}>
      <clipPath id="jpcirc"><circle cx="16" cy="16" r="16"/></clipPath>
      <g clipPath="url(#jpcirc)">
        <rect width="32" height="32" fill="#fff"/>
        <circle cx="16" cy="16" r="8" fill="#BC002D"/>
      </g>
    </svg>
  );
  // Canada
  if (c === "Canada" || c === "CA") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ borderRadius: "50%", flexShrink: 0 }}>
      <clipPath id="cacirc"><circle cx="16" cy="16" r="16"/></clipPath>
      <g clipPath="url(#cacirc)">
        <rect width="32" height="32" fill="#fff"/>
        <rect width="8" height="32" fill="#FF0000"/>
        <rect x="24" width="8" height="32" fill="#FF0000"/>
        <text x="16" y="21" textAnchor="middle" fontSize="14" fill="#FF0000">🍁</text>
      </g>
    </svg>
  );
  // Australia
  if (c === "Australia" || c === "AU") return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ borderRadius: "50%", flexShrink: 0 }}>
      <clipPath id="aucirc"><circle cx="16" cy="16" r="16"/></clipPath>
      <g clipPath="url(#aucirc)">
        <rect width="32" height="32" fill="#00008B"/>
        <text x="16" y="20" textAnchor="middle" fontSize="10" fill="#fff">AU</text>
      </g>
    </svg>
  );
  // Fallback — show 2-letter abbreviation in a circle
  const abbr = c.slice(0,2).toUpperCase();
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ borderRadius: "50%", flexShrink: 0 }}>
      <circle cx="16" cy="16" r="16" fill="#64748B"/>
      <text x="16" y="21" textAnchor="middle" fontSize="12" fontWeight="700" fill="#fff">{abbr}</text>
    </svg>
  );
}

// ─── Detail Strip — inline, hover shows tooltip via Portal ──
function DetailStrip({ d, onClick, onEdit }) {
  const [tip, setTip] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const phCfg   = PHASE_CONFIG[d.PHASENAME] || { color: "#6B7280", bg: "#F3F4F6" };
  const country = d.COUNTRYNAME || "";

  const tooltipEl = tip ? ReactDOM.createPortal(
    <div style={{
      position:"fixed", ...smartPos(pos.x, pos.y),
      zIndex: 99999,
      pointerEvents: "none",
      background: "#0F172A",
      color: "#fff",
      borderRadius: "14px",
      padding: "14px 16px",
      minWidth: "260px",
      maxWidth: "340px",
      fontSize: "12px",
      boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
      border: `1px solid ${phCfg.color}66`,
      lineHeight: "1.5",
    }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"10px",
        paddingBottom:"10px", borderBottom:"1px solid rgba(255,255,255,0.08)" }}>
        <div style={{ width:"28px", height:"28px", borderRadius:"6px",
          background:`${phCfg.color}22`, display:"flex", alignItems:"center",
          justifyContent:"center", flexShrink:0, border:`1px solid ${phCfg.color}44` }}>
          <span style={{ fontSize:"11px", fontWeight:"800", color:phCfg.color }}>
            {(d.PHASENAME||"?")[0]}
          </span>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:"800", fontSize:"14px", color:"#F1F5F9", lineHeight:1.2 }}>
            {d.BRANDNAME || (d.TITLE ? d.TITLE.split(" ").slice(0,4).join(" ") : "—")}
          </div>
        </div>
      </div>
      {/* Acronym */}
      {d.ACRONYMS && (
        <div style={{ color:"#FCD34D", fontWeight:"800", fontSize:"13px", marginBottom:"4px", letterSpacing:"0.4px" }}>
          {d.ACRONYMS}
        </div>
      )}
      {/* Indication */}
      {d.INDICATIONNAME && (
        <div style={{ color:"#A5B4FC", fontWeight:"600", fontSize:"12px", marginBottom:"6px" }}>
          {d.INDICATIONNAME}
        </div>
      )}
      {/* Title excerpt */}
      {d.TITLE && (
        <div style={{ color:"#94A3B8", fontSize:"11px", lineHeight:"1.5", marginBottom:"8px",
          fontStyle:"italic", borderLeft:`2px solid ${phCfg.color}55`, paddingLeft:"8px" }}>
          {d.TITLE.length > 120 ? d.TITLE.slice(0,120)+"…" : d.TITLE}
        </div>
      )}
      {/* Country */}
      {country && (
        <div style={{ display:"flex", alignItems:"center", gap:"6px", marginBottom:"6px" }}>
          <FlagIcon country={country} size={16}/>
          <span style={{ fontSize:"12px", color:"#CBD5E1", fontWeight:"500" }}>{country}</span>
        </div>
      )}
      {/* Sponsor */}
      {d.SPONSORNAME && (
        <div style={{ fontSize:"11px", color:"#64748B", marginBottom:"8px" }}>{d.SPONSORNAME}</div>
      )}
      {/* Footer */}
      <div style={{ display:"flex", alignItems:"center", gap:"10px",
        paddingTop:"8px", borderTop:"1px solid rgba(255,255,255,0.08)" }}>
        {d.LAUNCHDATE && (
          <div style={{ display:"flex", alignItems:"center", gap:"5px",
            color:"#6EE7B7", fontWeight:"700", fontSize:"12px" }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6EE7B7" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            {d.LAUNCHDATE}
          </div>
        )}
        <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:"5px" }}>
          <div style={{ width:"7px", height:"7px", borderRadius:"50%", background:phCfg.color }}/>
          <span style={{ fontSize:"11px", color:phCfg.color, fontWeight:"700" }}>{d.PHASENAME}</span>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div style={{ position: "relative" }}>
      {tooltipEl}
      <div
        onClick={e=>{ e.stopPropagation(); onClick?.(); }}
        onMouseEnter={e => { setTip(true); setPos({ x: e.clientX, y: e.clientY }); }}
        onMouseMove={e => setPos({ x: e.clientX, y: e.clientY })}
        onMouseLeave={() => setTip(false)}
        style={{
          display: "flex", alignItems: "center", gap: "6px",
          padding: "4px 8px", borderRadius: "6px", cursor: onClick?"pointer":"default",
          background: phCfg.bg, border: `1px solid ${phCfg.color}22`,
          transition: "box-shadow 0.15s",
          boxShadow: tip ? `0 2px 8px ${phCfg.color}33` : "none",
        }}>
        {/* Phase pill */}
        <span style={{ fontSize: "9px", fontWeight: "700", color: phCfg.color,
          background: "#fff", borderRadius: "3px", padding: "1px 5px",
          border: `1px solid ${phCfg.color}44`, whiteSpace: "nowrap", flexShrink: 0 }}>
          {d.PHASENAME || "—"}
        </span>
        {/* Circular flag */}
        {country && <FlagIcon country={country} size={15} />}
        {/* Indication */}
        <span style={{ fontSize: "10px", fontWeight: "600", color: "#374151",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
          {d.INDICATIONNAME || "—"}
        </span>
        {/* Launch date */}
        {d.LAUNCHDATE && (
          <span style={{ fontSize: "9px", color: "#9CA3AF", flexShrink: 0, whiteSpace: "nowrap" }}>
            🎯 {d.LAUNCHDATE.replace(/\d{2} /, "")}
          </span>
        )}
      </div>

    </div>
  );
}

// ─── Product Profile Tile ─────────────────────
function ProductTile({ product, onOpen, onOpenDetail, onEdit, isAdmin }) {
  const [hovered, setHovered]     = useState(false);
  const chatbot  = Array.isArray(product.CHATBOT) ? product.CHATBOT : [];
  const imgFiles = chatbot.filter(a => isImage(a.FILEPATH));
  const thumbUrl = imgFiles.length > 0 ? THUMB_BASE + fileName(imgFiles[0].FILEPATH) : null;
  const logoUrl  = product.COMPANY_LOGO ? LOGO_BASE + product.COMPANY_LOGO : null;
  const details  = Array.isArray(product.PRODUCT_DETAIL) ? product.PRODUCT_DETAIL : [];
  // Deduplicate phases shown
  const phases   = [...new Set(details.map(d => d.PHASENAME).filter(Boolean))];
  const fav      = product.MYFAVOURITES === "YES";
  const liked    = product.MYLIKES === "YES";

  return (
    <div
      onClick={onOpen ? () => onOpen(product) : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff",
        borderRadius: "12px",
        border: `1px solid ${hovered ? "#6366F1" : "#E5E7EB"}`,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.18s",
        boxShadow: hovered ? "0 8px 24px rgba(99,102,241,0.12)" : "0 1px 4px rgba(0,0,0,0.05)",
        transform: hovered ? "translateY(-2px)" : "none",
      }}>

      {/* Top section */}
      <div style={{ padding: "12px 14px 0" }}>
        {/* Top row: logo + name + thumb */}
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
          {/* Company logo */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "3px", flexShrink: 0, width: "42px" }}>
            <div style={{ width: "38px", height: "38px", borderRadius: "8px", border: "1px solid #E5E7EB", background: "#F9FAFB", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {logoUrl
                ? <img src={logoUrl} alt={product.SPONSORNAME} style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
                : <span style={{ fontSize: "13px", fontWeight: "700", color: "#6366F1" }}>{(product.SPONSORNAME || "?")[0]}</span>}
            </div>
            {product.SPONSORNAME && (
              <div style={{ fontSize: "9px", color: "#9CA3AF", textAlign: "center", lineHeight: "1.2", wordBreak: "break-word", maxWidth: "42px", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {product.SPONSORNAME}
              </div>
            )}
          </div>

          {/* Brand + meta */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", marginBottom: "2px" }}>
              <span style={{ fontSize: "15px", fontWeight: "700", color: "#111827" }}>{product.BRANDNAME}</span>
              {fav && <span style={{ fontSize: "11px" }}>⭐</span>}
            </div>
            <div style={{ fontSize: "12px", color: "#6366F1", fontWeight: "500", marginBottom: "3px" }}>{product.GENERIC}</div>
            <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap" }}>
              {product.MOANAME && <span style={{ fontSize: "11px", color: "#6B7280" }}>{product.MOANAME}</span>}
              {product.DELIVERY && <><span style={{ color: "#D1D5DB" }}>·</span><DeliveryBadge delivery={product.DELIVERY} /></>}
            </div>
          </div>

          {/* Thumbnail */}
          {thumbUrl && (
            <div style={{ width: "54px", height: "54px", borderRadius: "8px", border: "1px solid #E5E7EB", overflow: "hidden", flexShrink: 0 }}>
              <img src={thumbUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", padding: "2px" }} onError={e => e.target.parentNode.style.display = "none"} />
            </div>
          )}
        </div>

        {/* Phase pills */}
        {phases.length > 0 && (
          <div style={{ display: "flex", gap: "5px", flexWrap: "wrap", marginTop: "8px" }}>
            {phases.map(p => <PhasePill key={p} phase={p} />)}
            {details.length > 0 && (
              <span style={{ fontSize: "11px", color: "#9CA3AF", marginLeft: "2px" }}>
                {details.length} trial{details.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        )}
      </div>

      {/* PRODUCT_DETAIL strips — always visible, hover for detail */}
      {details.length > 0 && (
        <div style={{ padding: "8px 14px 4px 14px", display: "flex", flexDirection: "column", gap: "4px",
          borderTop: "1px solid #F1F5F9" }}>
          {details.map((d, i) => (
            <DetailStrip key={d.DOCUMENTID || i} d={d}
              onClick={()=>onOpenDetail?.(d)}
              onEdit={isAdmin?()=>onEdit?.(d):undefined}/>
          ))}
        </div>
      )}

      {/* Stats row — bottom of card */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px",
        padding: "7px 14px", borderTop: "1px solid #F1F5F9" }}>
        {fav && <PPIcon name="star" size={12} color="#EF9F27" />}
        <span style={{ fontSize: "11px", color: "#9CA3AF", flex: 1 }}>{product.DISPLAY_TIME}</span>

        {[
          { n: "attach", v: product.COUNT      || 0, active: false },
          { n: "like",   v: product.LIKES       || 0, active: liked },
          { n: "chat",   v: product.COMMENTCOUNT|| 0, active: false },
        ].map(s => (
          <div key={s.n} style={{ display: "flex", alignItems: "center", gap: "3px", fontSize: "11px",
            color: s.active ? "#6366F1" : "#9CA3AF", fontWeight: s.active ? "600" : "400" }}>
            <PPIcon name={s.n} size={12} color={s.active ? "#6366F1" : "#9CA3AF"} />
            <span>{s.v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── Flag map ─────────────────────────────────
const FLAG = {
  "US":"🇺🇸","USA":"🇺🇸","United States":"🇺🇸",
  "EU":"🇪🇺","Europe":"🇪🇺","European Union":"🇪🇺",
  "Global":"🌐","International":"🌐","Worldwide":"🌐",
  "UK":"🇬🇧","United Kingdom":"🇬🇧",
  "Japan":"🇯🇵","JP":"🇯🇵",
  "China":"🇨🇳","CN":"🇨🇳",
  "Canada":"🇨🇦","CA":"🇨🇦",
  "Australia":"🇦🇺","AU":"🇦🇺",
  "India":"🇮🇳","IN":"🇮🇳",
  "South Korea":"🇰🇷","Korea":"🇰🇷","KR":"🇰🇷",
  "Germany":"🇩🇪","DE":"🇩🇪",
  "France":"🇫🇷","FR":"🇫🇷",
  "Italy":"🇮🇹","IT":"🇮🇹",
  "Spain":"🇪🇸","ES":"🇪🇸",
};
const getFlag = c => FLAG[c] || "🌍";

// ─── Phase palette ────────────────────────────
const PHASE_ORDER  = ["Approved","Phase III","Phase II","Phase I","Other"];
const PHASE_CFG = {
  "Approved":  { bar:"#059669", grad:"linear-gradient(90deg,#059669,#34D399)", bg:"#ECFDF5", text:"#065F46", dot:"#10B981" },
  "Phase III": { bar:"#1D4ED8", grad:"linear-gradient(90deg,#1D4ED8,#60A5FA)", bg:"#EFF6FF", text:"#1E3A8A", dot:"#3B82F6" },
  "Phase II":  { bar:"#7C3AED", grad:"linear-gradient(90deg,#7C3AED,#C084FC)", bg:"#F5F3FF", text:"#4C1D95", dot:"#A855F7" },
  "Phase I":   { bar:"#0891B2", grad:"linear-gradient(90deg,#0891B2,#67E8F9)", bg:"#ECFEFF", text:"#164E63", dot:"#06B6D4" },
  "Other":     { bar:"#6B7280", grad:"linear-gradient(90deg,#6B7280,#D1D5DB)", bg:"#F9FAFB", text:"#374151", dot:"#9CA3AF" },
};
const PRI_CFG = {
  HIGH:   { color:"#EF4444", bg:"#FEF2F2", label:"HIGH"   },
  MEDIUM: { color:"#F59E0B", bg:"#FFFBEB", label:"MED"    },
  LOW:    { color:"#6B7280", bg:"#F9FAFB", label:"LOW"    },
};
const PRI_COLORS = { HIGH:"#EF4444", MEDIUM:"#F59E0B", LOW:"#9CA3AF" };

// ─── Parse launch date → quarter ──────────────
function parseLaunch(raw) {
  if (!raw) return null;
  const MON = {Jan:1,Feb:2,Mar:3,Apr:4,May:5,Jun:6,Jul:7,Aug:8,Sep:9,Oct:10,Nov:11,Dec:12};
  const m = raw.match(/(\d{1,2})\s+([A-Za-z]+)[,\s]+(\d{4})/);
  if (!m) return null;
  const mon = MON[m[2]] || 1;
  const yr  = parseInt(m[3]);
  const q   = Math.ceil(mon / 3);
  return { year:yr, q, label:`Q${q} ${yr}`, sort:yr*10+q };
}

// ─── Flatten all PRODUCT_DETAIL entries ────────
function flattenEntries(products) {
  const out = [];
  products.forEach(p => {
    const details = Array.isArray(p.PRODUCT_DETAIL) ? p.PRODUCT_DETAIL : [];
    if (!details.length) {
      out.push({ brand:p.BRANDNAME, sponsor:p.SPONSORNAME, priority:p.PRIORITY,
        indication:"", acronym:"", country:"", logo:p.COMPANY_LOGO,
        launchDate:"", parsed:null, phase:"Other",
        uid:`${p.DOCUMENTID}_nd`, docId:p.DOCUMENTID });
    } else {
      details.forEach((d,di) => {
        out.push({ brand:p.BRANDNAME, sponsor:p.SPONSORNAME, priority:p.PRIORITY,
          indication:d.INDICATIONNAME||"", acronym:d.ACRONYMS||"",
          country:d.COUNTRYNAME||"", logo:p.COMPANY_LOGO,
          launchDate:d.LAUNCHDATE||"", parsed:parseLaunch(d.LAUNCHDATE),
          phase:d.PHASENAME||"Other",
          uid:`${p.DOCUMENTID}_${d.DOCUMENTID||di}`, docId:p.DOCUMENTID });
      });
    }
  });
  return out;
}

function buildChartData(products) {
  const entries  = flattenEntries(products);
  const phaseMap = {};
  entries.forEach(e => {
    if (!phaseMap[e.phase]) phaseMap[e.phase] = [];
    phaseMap[e.phase].push(e);
  });
  const donut = PHASE_ORDER
    .map(ph => ({ phase:ph, count:(phaseMap[ph]||[]).length, ...PHASE_CFG[ph] }))
    .filter(d => d.count > 0);
  return { phaseMap, donut, entries };
}

// ─── Shared tooltip ───────────────────────────
function ChartTooltip({ tip }) {
  if (!tip) return null;
  const { item, x, y } = tip;
  const pc = PHASE_CFG[item.phase] || PHASE_CFG["Other"];
  return (
    <div style={{ position:"fixed", ...smartPos(x, y), zIndex:9999, pointerEvents:"none",
      background:"#0F172A", color:"#fff", borderRadius:"12px", padding:"12px 16px",
      maxWidth:"260px", boxShadow:"0 8px 32px rgba(0,0,0,0.4)", lineHeight:"1.6",
      border:`1px solid ${pc.bar}44` }}>
      <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"6px" }}>
        {item.logo && <img src={LOGO_BASE+item.logo} alt="" style={{ width:"20px",height:"20px",objectFit:"contain",borderRadius:"4px" }} onError={e=>e.target.style.display="none"} />}
        <span style={{ fontWeight:"800", fontSize:"13px" }}>{item.brand}</span>
      </div>
      {item.acronym    && <div style={{ color:"#FCD34D", fontWeight:"700", fontSize:"12px" }}>{item.acronym}</div>}
      {item.indication && <div style={{ color:"#A5B4FC", fontSize:"12px" }}>{item.indication}</div>}
      {item.country    && <div style={{ display:"flex",alignItems:"center",gap:"6px",color:"#D1D5DB",fontSize:"12px",marginTop:"4px" }}><FlagIcon country={item.country} size={16}/><span>{item.country}</span></div>}
      {item.sponsor    && <div style={{ color:"#6B7280", fontSize:"11px" }}>{item.sponsor}</div>}
      {item.launchDate && <div style={{ color:"#6EE7B7", fontSize:"12px", fontWeight:"600", marginTop:"4px" }}>🎯 {item.launchDate}</div>}
      <div style={{ marginTop:"6px", display:"flex", alignItems:"center", gap:"6px" }}>
        <div style={{ width:"8px", height:"8px", borderRadius:"50%", background:pc.dot }} />
        <span style={{ fontSize:"11px", color:pc.dot, fontWeight:"600" }}>{item.phase}</span>
      </div>
    </div>
  );
}

// ─── Shared mini card ─────────────────────────
function MiniCard({ item, onEnter, onLeave, subId }) {
  const pc   = PHASE_CFG[item.phase] || PHASE_CFG["Other"];
  return (
    <div onMouseEnter={onEnter} onMouseLeave={onLeave}
      onClick={()=>item.docId&&openDocTab(item.docId, subId)}
      style={{ background:"#fff", borderRadius:"8px", padding:"6px 9px",
        cursor: item.docId ? "pointer" : "default",
        border:`1px solid ${pc.bar}33`, borderLeft:`3px solid ${pc.bar}`,
        boxShadow:"0 1px 4px rgba(0,0,0,0.06)",
        cursor:"default", minWidth:"130px", maxWidth:"180px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:"4px", marginBottom:"2px" }}>
        {item.logo && <img src={LOGO_BASE+item.logo} alt="" style={{ width:"14px",height:"14px",objectFit:"contain",flexShrink:0 }} onError={e=>e.target.style.display="none"} />}
        <span style={{ fontSize:"11px",fontWeight:"700",color:"#111827",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1 }}>{item.brand}</span>
        {item.country && <FlagIcon country={item.country} size={14}/>}
      </div>
      {item.acronym    && <div style={{ fontSize:"9px",fontWeight:"700",color:"#B45309",background:"#FEF3C7",borderRadius:"3px",padding:"1px 5px",display:"inline-block",marginBottom:"1px" }}>{item.acronym}</div>}
      {item.indication && <div style={{ fontSize:"10px",color:"#6366F1",fontWeight:"600",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{item.indication}</div>}
      {item.launchDate && <div style={{ fontSize:"9px",color:"#9CA3AF",marginTop:"1px" }}>🎯 {item.launchDate}</div>}
    </div>
  );
}

// ─── Priority legend ──────────────────────────
function PriLegend() {
  return (
    <div style={{ display:"flex", gap:"12px", alignItems:"center" }}>
      {Object.entries(PRI_CFG).map(([k,v]) => (
        <div key={k} style={{ display:"flex", alignItems:"center", gap:"4px" }}>
          <div style={{ width:"10px",height:"10px",borderRadius:"2px",background:v.color }} />
          <span style={{ fontSize:"10px",color:"#6B7280",fontWeight:"500" }}>{k}</span>
        </div>
      ))}
    </div>
  );
}

// ─── 1. OVERVIEW: Donut + priority bars + delivery ─────────────────────────
function DonutChart({ donut, total }) {
  const [hovered, setHovered] = useState(null);
  const R=72,cx=92,cy=92,stroke=28, circ=2*Math.PI*R;
  let offset=0;
  const slices = donut.map(d => {
    const dash=(d.count/total)*circ;
    const s={...d,dash,gap:circ-dash,offset};
    offset+=dash; return s;
  });
  const active = hovered !== null ? slices[hovered] : null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:"28px" }}>
      <div style={{ position:"relative" }}>
        <svg width="184" height="184" viewBox="0 0 184 184">
          <circle cx={cx} cy={cy} r={R} fill="none" stroke="#F1F5F9" strokeWidth={stroke} />
          {slices.map((s,i) => (
            <circle key={i} cx={cx} cy={cy} r={R} fill="none"
              stroke={s.bar} strokeWidth={hovered===i ? stroke+4 : stroke}
              strokeDasharray={`${s.dash} ${s.gap}`}
              strokeDashoffset={-s.offset+circ*0.25}
              style={{ cursor:"pointer", transition:"stroke-width 0.2s" }}
              onMouseEnter={()=>setHovered(i)} onMouseLeave={()=>setHovered(null)} />
          ))}
          {active ? <>
            <text x={cx} y={cy-10} textAnchor="middle" fontSize="22" fontWeight="800" fill="#111827">{active.count}</text>
            <text x={cx} y={cy+10} textAnchor="middle" fontSize="10" fill={active.bar} fontWeight="600">{active.phase}</text>
            <text x={cx} y={cy+26} textAnchor="middle" fontSize="11" fill="#9CA3AF">{Math.round(active.count/total*100)}%</text>
          </> : <>
            <text x={cx} y={cy-6} textAnchor="middle" fontSize="30" fontWeight="900" fill="#111827">{total}</text>
            <text x={cx} y={cy+16} textAnchor="middle" fontSize="11" fill="#9CA3AF">entries</text>
          </>}
        </svg>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
        {donut.map((d,i) => (
          <div key={d.phase} style={{ display:"flex", alignItems:"center", gap:"10px", padding:"6px 10px", borderRadius:"8px", background:hovered===i?d.bg:"transparent", cursor:"pointer", transition:"background 0.15s" }}
            onMouseEnter={()=>setHovered(i)} onMouseLeave={()=>setHovered(null)}>
            <div style={{ width:"12px",height:"12px",borderRadius:"3px",background:d.bar,flexShrink:0 }} />
            <span style={{ fontSize:"12px",color:"#374151",fontWeight:"500",minWidth:"80px" }}>{d.phase}</span>
            <span style={{ fontSize:"14px",fontWeight:"800",color:d.bar }}>{d.count}</span>
            <div style={{ width:"60px",height:"4px",borderRadius:"2px",background:"#F1F5F9",overflow:"hidden" }}>
              <div style={{ height:"100%",width:`${d.count/total*100}%`,background:d.grad }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Bullseye chart — Approved outermost, Phase I innermost
// Legend table: Indication · Brand · Sponsor per entry
function BullseyeChart({ phaseMap, svgRef, subId }) {
  const [tip, setTip]                 = useState(null);
  const [hoveredPhase, setHoveredPhase] = useState(null);
  const [hoveredUid, setHoveredUid]     = useState(null);
  const [filterPhase, setFilterPhase]   = useState(null);

  // Outer = Approved, inner = Phase I  (PHASE_ORDER is Approved→Phase I)
  const phases = PHASE_ORDER.filter(ph => (phaseMap[ph]||[]).length);
  // Ring assignment: Approved gets outermost ring, Phase I gets innermost
  const RING_W  = 44;   // ring band width
  const BASE_R  = 24;   // innermost radius
  const phaseRadii = {};
  phases.forEach((ph, i) => {
    phaseRadii[ph] = BASE_R + (phases.length - 1 - i) * RING_W;
  });

  const outerR = BASE_R + (phases.length - 1) * RING_W + RING_W / 2;
  const SIZE   = outerR * 2 + 140;   // no hard minimum — scales with phases
  const cx = SIZE / 2, cy = SIZE / 2;

  function itemsOnRing(phase) {
    const items = phaseMap[phase] || [];
    const r     = phaseRadii[phase];
    if (!items.length) return [];
    return items.map((item, i) => {
      const angle = -Math.PI / 2 + (2 * Math.PI * i) / items.length;
      return { ...item, x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle), angle };
    });
  }

  // Build flat legend list ordered by phase
  const legendEntries = phases.flatMap(ph =>
    (phaseMap[ph] || []).map(e => ({ ...e, phCfg: PHASE_CFG[ph] || PHASE_CFG["Other"] }))
  );

  return (
    <div style={{ width:"100%", display:"flex", gap:"24px", alignItems:"flex-start", flexWrap:"wrap" }}>

      {/* ── SVG Bullseye ── */}
      <div style={{ flex:"1 1 auto", minWidth:0, display:"flex", flexDirection:"column", alignItems:"center", gap:"16px", maxWidth:`${SIZE}px`, width:"100%" }}>
        <svg ref={svgRef} width="100%" height="100%" viewBox={`0 0 ${SIZE} ${SIZE}`} style={{maxWidth:`${SIZE}px`,display:"block"}}>
          <defs>
            {phases.map(ph => {
              const cfg = PHASE_CFG[ph];
              const id  = `bg_${ph.replace(/\W/g,"_")}`;
              return (
                <radialGradient key={id} id={id} cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#fff" stopOpacity="0" />
                  <stop offset="100%" stopColor={cfg.bar} stopOpacity="0.12" />
                </radialGradient>
              );
            })}
          </defs>

          {/* Rings — from outermost (Approved) to innermost, painted big→small */}
          {[...phases].map((ph) => {
            const cfg  = PHASE_CFG[ph];
            const r    = phaseRadii[ph];
            const band = RING_W / 2;
            const isHov = hoveredPhase === ph;
            const id = `bg_${ph.replace(/\W/g,"_")}`;
            return (
              <g key={`ring_${ph}`}
                onMouseEnter={() => setHoveredPhase(ph)}
                onMouseLeave={() => setHoveredPhase(null)}
                style={{ cursor:"pointer" }}>
                {/* Filled annulus using two circles */}
                <circle cx={cx} cy={cy} r={r + band}
                  fill={`url(#${id})`} />
                <circle cx={cx} cy={cy} r={r + band}
                  fill="none"
                  stroke={cfg.bar}
                  strokeWidth={isHov ? 2.5 : 1}
                  strokeOpacity={isHov ? 0.9 : filterPhase && filterPhase!==ph ? 0.1 : 0.3}
                opacity={filterPhase && filterPhase!==ph ? 0.35 : 1} />
                {/* Phase label arc — placed at top of ring */}
                <text x={cx} y={cy - r - band + 16}
                  textAnchor="middle" fontSize="9" fontWeight="800"
                  fill={cfg.bar} opacity={isHov ? 1 : 0.6}
                  style={{ pointerEvents:"none", userSelect:"none" }}>
                  {ph.toUpperCase()}
                </text>
              </g>
            );
          })}

          {/* White centre circle */}
          <circle cx={cx} cy={cy} r={BASE_R - 10} fill="white" stroke="#E2E8F0" strokeWidth="1.5" />
          <text x={cx} y={cy - 7} textAnchor="middle" fontSize="16" fontWeight="900" fill="#1E293B">
            {legendEntries.length}
          </text>
          <text x={cx} y={cy + 10} textAnchor="middle" fontSize="9" fill="#94A3B8" fontWeight="500">entries</text>

          {/* Dots + labels on each ring */}
          {phases.map(ph => {
            const cfg    = PHASE_CFG[ph];
            const placed = itemsOnRing(ph);
            if (filterPhase && filterPhase !== ph) return null;
            return placed.map(item => {
              const isRight  = item.x >= cx;
              const anchor   = isRight ? "start" : "end";
              const lx       = item.x + (isRight ? 14 : -14);
              const isHov    = hoveredPhase === ph || hoveredUid === item.uid;
              return (
                <g key={item.uid}
                  onMouseEnter={e => { setTip({ item, x:e.clientX, y:e.clientY }); setHoveredUid(item.uid); }}
                  onMouseLeave={() => { setTip(null); setHoveredUid(null); }}
                  onClick={()=>item.docId&&openDocTab(item.docId, subId)}
                  style={{ cursor:"pointer" }}>
                  {/* Tick line from ring edge to dot */}
                  <line
                    x1={item.x} y1={item.y}
                    x2={item.x + (isRight ? 10 : -10)} y2={item.y}
                    stroke={cfg.bar} strokeWidth="0.7" opacity="0.5" />
                  {/* Dot */}
                  <circle cx={item.x} cy={item.y} r={isHov ? 5.5 : 3.5}
                    fill={cfg.bar} stroke="white" strokeWidth="1.5"
                    style={{ transition:"r 0.15s" }} />
                  {/* Brand */}
                  <text x={lx} y={item.y - 2} textAnchor={anchor}
                    fontSize="8" fontWeight="700" fill={isHov ? cfg.bar : "#1E293B"}
                    style={{ userSelect:"none" }}>
                    {item.brand}
                  </text>
                  {/* Indication + flag */}
                  <text x={lx} y={item.y + 9} textAnchor={anchor}
                    fontSize="7" fill="#6B7280"
                    style={{ userSelect:"none" }}>
                    {[item.indication && item.indication.length > 18 ? item.indication.slice(0,16)+"…" : item.indication, item.country ? item.country.slice(0,2).toUpperCase() : ""].filter(Boolean).join(" · ")}
                  </text>
                </g>
              );
            });
          })}
        </svg>

        {/* Phase filter pills — click to filter dots */}
        <div style={{ display:"flex", flexWrap:"wrap", gap:"8px", justifyContent:"center" }}>
          {phases.map(ph => {
            const cfg    = PHASE_CFG[ph];
            const count  = (phaseMap[ph]||[]).length;
            const active = filterPhase === ph;
            const isHov  = hoveredPhase === ph;
            return (
              <div key={ph}
                onClick={() => setFilterPhase(active ? null : ph)}
                onMouseEnter={() => setHoveredPhase(ph)}
                onMouseLeave={() => setHoveredPhase(null)}
                style={{ display:"flex", alignItems:"center", gap:"6px", padding:"5px 14px",
                  borderRadius:"20px", cursor:"pointer", transition:"all 0.18s",
                  background: active ? cfg.bg : isHov ? cfg.bg+"80" : "#F8FAFC",
                  border:`1.5px solid ${active ? cfg.bar : isHov ? cfg.bar+"66" : "#E5E7EB"}`,
                  boxShadow: active ? `0 0 0 2px ${cfg.bar}33` : "none" }}>
                <div style={{ width:"9px",height:"9px",borderRadius:"50%",background:cfg.bar }} />
                <span style={{ fontSize:"11px",fontWeight:"700",color:cfg.text }}>{ph}</span>
                <span style={{ fontSize:"12px",fontWeight:"900",color:cfg.bar }}>{count}</span>
                {active && <span style={{ fontSize:"10px",color:cfg.bar,fontWeight:"800" }}>✕</span>}
              </div>
            );
          })}
          {filterPhase && (
            <div onClick={() => setFilterPhase(null)}
              style={{ display:"flex", alignItems:"center", padding:"5px 12px",
                borderRadius:"20px", cursor:"pointer", border:"1px solid #FECACA",
                background:"#FEF2F2", color:"#EF4444", fontSize:"11px", fontWeight:"600" }}>
              Clear
            </div>
          )}
        </div>
      </div>

      {/* ── Legend table: Indication · Brand · Sponsor ── */}
      <div style={{ flex:1, minWidth:"280px", maxHeight:`${SIZE}px`, overflowY:"auto" }}>
        {phases.map(ph => {
          const cfg   = PHASE_CFG[ph];
          const items = phaseMap[ph] || [];
          if (!items.length) return null;
          return (
            <div key={ph} style={{ marginBottom:"16px" }}>
              {/* Phase header */}
              <div style={{ display:"flex", alignItems:"center", gap:"8px", padding:"6px 10px",
                borderRadius:"8px", background:cfg.bg, marginBottom:"6px" }}>
                <div style={{ width:"8px",height:"8px",borderRadius:"50%",background:cfg.bar,flexShrink:0 }} />
                <span style={{ fontSize:"11px",fontWeight:"800",color:cfg.text,textTransform:"uppercase",letterSpacing:"0.5px" }}>{ph}</span>
                <span style={{ fontSize:"12px",fontWeight:"900",color:cfg.bar,marginLeft:"auto" }}>{items.length}</span>
              </div>
              {/* Column headers */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4px", padding:"0 6px 4px",
                borderBottom:`1px solid ${cfg.bar}22` }}>
                {["Product","Indication"].map(h => (
                  <span key={h} style={{ fontSize:"9px",fontWeight:"700",color:"#9CA3AF",textTransform:"uppercase",letterSpacing:"0.5px" }}>{h}</span>
                ))}
              </div>
              {/* Rows */}
              {items.map(item => {
                const isHov = hoveredUid === item.uid;
                return (
                  <div key={item.uid}
                    onMouseEnter={() => setHoveredUid(item.uid)}
                    onMouseLeave={() => setHoveredUid(null)}
                    style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4px",
                      padding:"5px 6px", borderRadius:"6px", cursor:"default",
                      background: isHov ? cfg.bg : "transparent",
                      transition:"background 0.15s",
                      borderBottom:`1px solid ${cfg.bar}11` }}>
                    <div style={{ display:"flex",alignItems:"center",gap:"4px",overflow:"hidden" }}>
                      {item.logo && <img src={LOGO_BASE+item.logo} alt="" style={{ width:"12px",height:"12px",objectFit:"contain",flexShrink:0 }} onError={e=>e.target.style.display="none"} />}
                      <span style={{ fontSize:"10px",fontWeight:"700",color:"#1E293B",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
                        {item.brand}
                      </span>
                      {item.country && <FlagIcon country={item.country} size={14}/>}
                    </div>
                    <span style={{ fontSize:"10px",color:"#6366F1",fontWeight:"600",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
                      {item.indication || "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <ChartTooltip tip={tip} />
    </div>
  );
}


// ─── Bullseye section with screenshot button ──────────────────────────────────
function BullseyeSection({ phaseMap, subId }) {
  const svgRef   = useRef(null);
  const [saving, setSaving] = useState(false);

  const exportJpeg = () => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    setSaving(true);

    // ── 1. Get intrinsic SVG dimensions from viewBox ──────────────────────────
    const vb      = svgEl.getAttribute("viewBox").split(" ").map(Number);
    const vbW     = vb[2], vbH = vb[3];

    // ── 2. Scale up 2× for high DPI quality ─────────────────────────────────
    const SCALE   = 2;
    const PAD     = 48 * SCALE;
    const TITLE_H = 52 * SCALE;
    const cW      = vbW * SCALE + PAD * 2;
    const cH      = vbH * SCALE + PAD * 2 + TITLE_H;

    // ── 3. Build a self-contained SVG string with all colours inlined ────────
    // Clone and force explicit width/height so the Image loads at full size
    const clone = svgEl.cloneNode(true);
    clone.setAttribute("width",  vbW);
    clone.setAttribute("height", vbH);
    clone.setAttribute("xmlns",  "http://www.w3.org/2000/svg");
    // Remove count/entries text nodes from the exported image
    clone.querySelectorAll("text").forEach(t => {
      const txt = t.textContent.trim();
      if (/^\d+$/.test(txt) || txt === "entries" || txt === "products") t.remove();
    });
    // Walk every element and copy computed styles as inline style
    // (avoids CSS class resolution issues in the canvas)
    clone.querySelectorAll("*").forEach(el => {
      try {
        const cs = window.getComputedStyle(el);
        const keep = ["fill","stroke","stroke-width","opacity","font-size",
                      "font-family","font-weight","text-anchor","dominant-baseline"];
        keep.forEach(p => {
          const v = cs.getPropertyValue(p);
          if (v) el.style[p] = v;
        });
      } catch(_) {}
    });

    const xml    = new XMLSerializer().serializeToString(clone);
    const b64    = btoa(unescape(encodeURIComponent(xml)));
    const dataUrl = `data:image/svg+xml;base64,${b64}`;

    // ── 4. Draw onto canvas ───────────────────────────────────────────────────
    const canvas = document.createElement("canvas");
    canvas.width  = cW;
    canvas.height = cH;
    const ctx = canvas.getContext("2d");

    // White background
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, cW, cH);

    // Subtle header bar
    ctx.fillStyle = "#F8FAFC";
    ctx.fillRect(0, 0, cW, TITLE_H);
    ctx.strokeStyle = "#E5E7EB";
    ctx.lineWidth = SCALE;
    ctx.beginPath();
    ctx.moveTo(0, TITLE_H);
    ctx.lineTo(cW, TITLE_H);
    ctx.stroke();

    // Title text
    ctx.fillStyle  = "#111827";
    ctx.font       = `bold ${16 * SCALE}px Arial, sans-serif`;
    ctx.fillText("Pipeline Bullseye", PAD, TITLE_H / 2 + 5 * SCALE);



    // Draw SVG image
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, PAD, TITLE_H + PAD / 2, vbW * SCALE, vbH * SCALE);

      // Watermark bottom-right
      ctx.fillStyle = "#D1D5DB";
      ctx.font      = `${9 * SCALE}px Arial, sans-serif`;
      ctx.textAlign = "right";
      ctx.fillText("PharmaVision", cW - PAD / 2, cH - PAD / 3);

      // Download
      canvas.toBlob(blob => {
        const url  = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href  = url;
        link.download = `pipeline_bullseye_${Math.floor(Math.random()*900000+100000)}.jpg`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setSaving(false);
      }, "image/jpeg", 0.95);
    };
    img.onerror = () => setSaving(false);
    img.src = dataUrl;
  };

  return (
    <div style={{ width:"100%", background:"#fff", borderRadius:"16px",
      border:"1px solid #E5E7EB", padding:"16px",
      boxShadow:"0 1px 8px rgba(0,0,0,0.06)",
      display:"flex", flexDirection:"column", alignItems:"center" }}>

      {/* Header row */}
      <div style={{ display:"flex", alignItems:"center", width:"100%", marginBottom:"4px" }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:"13px", fontWeight:"700", color:"#374151",
            letterSpacing:"0.3px" }}>PIPELINE BULLSEYE</div>
          <div style={{ fontSize:"11px", color:"#9CA3AF", marginTop:"2px" }}>
            Each ring = phase · Each dot = trial entry · Hover to explore
          </div>
        </div>
        <button onClick={exportJpeg} disabled={saving}
          style={{ display:"flex", alignItems:"center", gap:"5px",
            padding:"5px 12px", borderRadius:"7px",
            border:"1px solid #E5E7EB", background:"#F9FAFB",
            color:"#374151", fontSize:"11px", fontWeight:"600",
            cursor: saving ? "wait" : "pointer",
            opacity: saving ? 0.6 : 1,
            transition:"all 0.15s", flexShrink:0 }}>
          {saving ? (
            <>
              <div style={{ width:"11px", height:"11px", border:"2px solid #CBD5E1",
                borderTopColor:"#374151", borderRadius:"50%",
                animation:"spin 0.7s linear infinite" }}/>
              Saving...
            </>
          ) : (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <path d="M21 15l-5-5L5 21"/>
              </svg>
              Save as JPEG
            </>
          )}
        </button>
        <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
      </div>

      {/* Chart */}
      <div style={{ width:"100%", display:"flex", justifyContent:"center" }}>
        <BullseyeChart phaseMap={phaseMap} svgRef={svgRef} subId={subId}/>
      </div>
    </div>
  );
}

function OverviewTab({ products, donut, total, phaseMap, subId }) {
  // Top indications across all PRODUCT_DETAIL entries
  const allDetails = products.flatMap(p => Array.isArray(p.PRODUCT_DETAIL) ? p.PRODUCT_DETAIL : []);
  const indCounts  = {};
  allDetails.forEach(d => {
    const ind = d.INDICATIONNAME;
    if (ind) indCounts[ind] = (indCounts[ind]||0) + 1;
  });
  const topInds = Object.entries(indCounts).sort((a,b)=>b[1]-a[1]).slice(0,8);
  const maxInd  = topInds[0]?.[1] || 1;

  return (
    <div style={{ display:"flex", gap:"16px", flexWrap:"wrap" }}>
      {/* Donut */}
      <div style={{ background:"#fff",borderRadius:"16px",border:"1px solid #E5E7EB",padding:"24px",boxShadow:"0 1px 8px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize:"13px",fontWeight:"700",color:"#374151",marginBottom:"16px",letterSpacing:"0.3px" }}>PHASE DISTRIBUTION</div>
        <DonutChart donut={donut} total={total} />
      </div>

      {/* Indication breakdown + Delivery */}
      <div style={{ flex:1, minWidth:"240px", display:"flex", flexDirection:"column", gap:"12px" }}>
        <div style={{ background:"#fff",borderRadius:"16px",border:"1px solid #E5E7EB",padding:"20px",boxShadow:"0 1px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize:"13px",fontWeight:"700",color:"#374151",marginBottom:"14px",letterSpacing:"0.3px" }}>TOP INDICATIONS</div>
          {topInds.map(([ind, count]) => (
            <div key={ind} style={{ marginBottom:"11px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"4px" }}>
                <span style={{ fontSize:"12px",color:"#374151",fontWeight:"500",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"160px" }}>{ind}</span>
                <span style={{ fontSize:"12px",fontWeight:"700",color:"#6366F1",flexShrink:0,marginLeft:"8px" }}>{count}</span>
              </div>
              <div style={{ height:"6px",borderRadius:"3px",background:"#F1F5F9",overflow:"hidden" }}>
                <div style={{ height:"100%",width:`${count/maxInd*100}%`,background:"linear-gradient(90deg,#6366F1,#A855F7)",borderRadius:"3px",transition:"width 0.6s ease" }} />
              </div>
            </div>
          ))}
        </div>
        <div style={{ background:"#fff",borderRadius:"16px",border:"1px solid #E5E7EB",padding:"20px",boxShadow:"0 1px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize:"13px",fontWeight:"700",color:"#374151",marginBottom:"12px",letterSpacing:"0.3px" }}>DELIVERY ROUTE</div>
          <div style={{ display:"flex",flexWrap:"wrap",gap:"8px" }}>
            {[...new Set(products.map(p=>p.DELIVERY).filter(Boolean))].map(del => {
              const count = products.filter(p=>p.DELIVERY===del).length;
              return (
                <div key={del} style={{ background:"#F8FAFC",border:"1px solid #E5E7EB",borderRadius:"8px",padding:"6px 12px",display:"flex",gap:"6px",alignItems:"center" }}>
                  <span style={{ fontSize:"12px",fontWeight:"600",color:"#374151" }}>{del}</span>
                  <span style={{ fontSize:"11px",fontWeight:"700",color:"#6366F1",background:"#EEF2FF",borderRadius:"4px",padding:"1px 6px" }}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bullseye — full width */}
      <BullseyeSection phaseMap={phaseMap} subId={subId}/>
    </div>
  );
}

// ─── 2. TIMELINE: Gantt-style phase × quarter grid ────────────────────────
function TimelineTab({ phaseMap, subId }) {
  const [tip, setTip] = useState(null);
  const scrollRef = useRef(null);
  const allItems = PHASE_ORDER.flatMap(ph => phaseMap[ph]||[]);
  const qs = [...new Set(allItems.filter(e=>e.parsed).map(e=>e.parsed.label))]
    .sort((a,b)=>{
      const sa=allItems.find(e=>e.parsed?.label===a)?.parsed?.sort||0;
      const sb=allItems.find(e=>e.parsed?.label===b)?.parsed?.sort||0;
      return sa-sb;
    });
  const hasND   = allItems.some(e=>!e.parsed);
  const phases  = PHASE_ORDER.filter(ph=>(phaseMap[ph]||[]).length);
  const COL_W   = 170;

  // Current quarter label e.g. "Q2 2026"
  const now   = new Date();
  const curQ  = `Q${Math.ceil((now.getMonth()+1)/3)} ${now.getFullYear()}`;
  const curIdx = qs.indexOf(curQ);

  // Scroll to current quarter (or nearest past one) on mount
  useEffect(() => {
    if (!scrollRef.current) return;
    // Find the closest past-or-current quarter
    const targetIdx = curIdx >= 0 ? curIdx : qs.findIndex(q => {
      const [qn, yr] = q.split(" ");
      return parseInt(yr)*10+parseInt(qn[1]) >= parseInt(now.getFullYear())*10+Math.ceil((now.getMonth()+1)/3);
    });
    const idx = targetIdx >= 0 ? targetIdx : 0;
    // 110px for phase label col, then scroll to that column minus 1 col for context
    const scrollTo = 110 + Math.max(0, idx - 1) * COL_W;
    scrollRef.current.scrollLeft = scrollTo;
  }, [qs.length]);

  if (!qs.length && !hasND) return (
    <div style={{ padding:"3rem",textAlign:"center",color:"#9CA3AF",fontSize:"14px" }}>No launch date data available</div>
  );

  return (
    <div>
      <div ref={scrollRef} style={{ overflowX:"auto", borderRadius:"12px", border:"1px solid #E5E7EB", background:"#fff", boxShadow:"0 1px 8px rgba(0,0,0,0.06)" }}>
        <div style={{ minWidth:`${110+qs.length*COL_W+(hasND?150:0)}px` }}>
          {/* Header */}
          <div style={{ display:"flex", background:"#F8FAFC", borderBottom:"2px solid #E5E7EB", position:"sticky", top:0, zIndex:3 }}>
            <div style={{ width:"110px",flexShrink:0,padding:"10px 14px",display:"flex",alignItems:"center" }}>
              <span style={{ fontSize:"10px",fontWeight:"700",color:"#9CA3AF",letterSpacing:"0.8px",textTransform:"uppercase" }}>Phase</span>
            </div>
            {qs.map(q => {
              const [qn,yr]=q.split(" ");
              const isCur = q === curQ;
              return (
                <div key={q} style={{ width:`${COL_W}px`,flexShrink:0,padding:"10px",textAlign:"center",
                  borderLeft:"1px solid #E5E7EB",
                  background: isCur ? "#EEF2FF" : "transparent",
                  position:"relative" }}>
                  {isCur && <div style={{ position:"absolute",top:0,left:0,right:0,height:"3px",background:"#6366F1",borderRadius:"2px 2px 0 0" }}/>}
                  <div style={{ fontSize:"13px",fontWeight:"800",color: isCur ? "#6366F1" : "#1E293B" }}>{qn}</div>
                  <div style={{ fontSize:"11px",color: isCur ? "#818CF8" : "#64748B",fontWeight:"500" }}>{yr}</div>
                  {isCur && <div style={{ fontSize:"8px",color:"#6366F1",fontWeight:"700",marginTop:"1px" }}>TODAY</div>}
                </div>
              );
            })}
            {hasND && (
              <div style={{ width:"150px",flexShrink:0,padding:"10px",textAlign:"center",borderLeft:"1px dashed #CBD5E1" }}>
                <div style={{ fontSize:"11px",fontWeight:"600",color:"#94A3B8" }}>No Date</div>
              </div>
            )}
          </div>

          {/* Phase rows */}
          {phases.map((phase,pi) => {
            const items = phaseMap[phase]||[];
            const cfg   = PHASE_CFG[phase];
            return (
              <div key={phase} style={{ display:"flex", borderBottom:pi<phases.length-1?"1px solid #F1F5F9":"none", minHeight:"80px" }}>
                {/* Phase label */}
                <div style={{ width:"110px",flexShrink:0,padding:"12px 14px",borderRight:`4px solid ${cfg.bar}`,background:cfg.bg,display:"flex",flexDirection:"column",justifyContent:"center" }}>
                  <div style={{ fontSize:"10px",fontWeight:"800",color:cfg.text,textTransform:"uppercase",letterSpacing:"0.6px" }}>{phase}</div>
                  <div style={{ display:"flex",alignItems:"baseline",gap:"3px",marginTop:"2px" }}>
                    <span style={{ fontSize:"20px",fontWeight:"900",color:cfg.bar,lineHeight:1 }}>{items.length}</span>
                    <span style={{ fontSize:"9px",color:cfg.text,opacity:0.7 }}>entries</span>
                  </div>
                </div>
                {/* Quarter cells */}
                {qs.map(q => {
                  const cell=items.filter(e=>e.parsed?.label===q);
                  return (
                    <div key={q} style={{ width:`${COL_W}px`,flexShrink:0,padding:"8px",borderLeft:"1px solid #F1F5F9",display:"flex",flexDirection:"column",gap:"5px",background:cell.length?"#FAFBFF":"transparent" }}>
                      {cell.map(item => (
                        <MiniCard key={item.uid} item={item} subId={subId}
                          onEnter={e=>setTip({item,x:e.clientX,y:e.clientY})}
                          onLeave={()=>setTip(null)} />
                      ))}
                    </div>
                  );
                })}
                {/* No date */}
                {hasND && (
                  <div style={{ width:"150px",flexShrink:0,padding:"8px",borderLeft:"1px dashed #CBD5E1",display:"flex",flexDirection:"column",gap:"5px" }}>
                    {items.filter(e=>!e.parsed).map(item => (
                      <MiniCard key={item.uid} item={item}
                        onEnter={e=>setTip({item,x:e.clientX,y:e.clientY})}
                        onLeave={()=>setTip(null)} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <ChartTooltip tip={tip} />
    </div>
  );
}

// ─── 3. LANDSCAPE: Swimlane with gradient bars ────────────────────────────
function LandscapeTab({ phaseMap, subId }) {
  const [tip, setTip] = useState(null);
  const phases = PHASE_ORDER.filter(ph=>(phaseMap[ph]||[]).length);
  return (
    <div>
      <div style={{ borderRadius:"16px",border:"1px solid #E5E7EB",background:"#fff",overflow:"hidden",boxShadow:"0 1px 8px rgba(0,0,0,0.06)" }}>
        {phases.map((phase,pi) => {
          const items = phaseMap[phase]||[];
          const cfg   = PHASE_CFG[phase];
          return (
            <div key={phase}>
              {/* Phase header bar */}
              <div style={{ display:"flex",alignItems:"center",padding:"10px 16px",background:cfg.grad,gap:"12px" }}>
                <span style={{ fontSize:"11px",fontWeight:"800",color:"#fff",textTransform:"uppercase",letterSpacing:"0.8px",opacity:0.95 }}>{phase}</span>
                <span style={{ fontSize:"12px",fontWeight:"700",color:"#fff",background:"rgba(255,255,255,0.25)",borderRadius:"20px",padding:"1px 10px" }}>{items.length}</span>
              </div>
              {/* Cards */}
              <div style={{ padding:"12px 16px",display:"flex",flexWrap:"wrap",gap:"8px",borderBottom:pi<phases.length-1?"1px solid #F1F5F9":"none" }}>
                {items.map(item => (
                  <MiniCard key={item.uid} item={item} subId={subId}
                    onEnter={e=>setTip({item,x:e.clientX,y:e.clientY})}
                    onLeave={()=>setTip(null)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <ChartTooltip tip={tip} />
    </div>
  );
}

// ─── ChartView with tabs ──────────────────────
function ChartView({ products, subId }) {
  const [tab, setTab] = useState("overview");
  const { phaseMap, donut, entries } = buildChartData(products);
  const total = entries.length;

  if (total === 0) return (
    <div style={{ textAlign:"center",padding:"4rem",color:"#9CA3AF",fontSize:"14px" }}>No data to chart yet</div>
  );

  const TABS = [
    { id:"overview",  icon:"◎", label:"Overview"  },
    { id:"timeline",  icon:"▦", label:"Timeline"  },
    { id:"landscape", icon:"▤", label:"Landscape" },
  ];

  return (
    <div style={{ display:"flex",flexDirection:"column",gap:"16px" }}>
      {/* Tab bar */}
      <div style={{ display:"flex",gap:"0",background:"#F1F5F9",borderRadius:"12px",padding:"4px",alignSelf:"flex-start",boxShadow:"inset 0 1px 3px rgba(0,0,0,0.06)" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{ padding:"7px 20px",borderRadius:"9px",border:"none",cursor:"pointer",
              fontSize:"12px",fontWeight:"700",transition:"all 0.2s",display:"flex",alignItems:"center",gap:"6px",
              background: tab===t.id ? "#fff":"transparent",
              color:      tab===t.id ? "#6366F1":"#64748B",
              boxShadow:  tab===t.id ? "0 2px 8px rgba(99,102,241,0.15)":"none",
              transform:  tab===t.id ? "translateY(-1px)":"none",
            }}>
            <span style={{ fontSize:"14px" }}>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {tab==="overview"  && <OverviewTab  products={products} donut={donut} total={total} phaseMap={phaseMap} subId={subId}/>}
      {tab==="timeline"  && <TimelineTab  phaseMap={phaseMap} subId={subId}/>}
      {tab==="landscape" && <LandscapeTab phaseMap={phaseMap} subId={subId}/>}
    </div>
  );
}

function ViewToggle({ view, onChange }) {
  return (
    <div style={{ display: "flex", background: "#F3F4F6", borderRadius: "8px", padding: "3px", gap: "2px" }}>
      {[{ id: "list", label: "☰ List" }, { id: "chart", label: "◉ Chart" }].map(v => (
        <button key={v.id} onClick={() => onChange(v.id)}
          style={{ padding: "5px 14px", borderRadius: "6px", border: "none", cursor: "pointer", fontSize: "12px", fontWeight: "600", transition: "all 0.15s",
            background: view === v.id ? "#fff" : "transparent",
            color:      view === v.id ? "#6366F1" : "#6B7280",
            boxShadow:  view === v.id ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
          }}>{v.label}</button>
      ))}
    </div>
  );
}

// ─── Search bar ───────────────────────────────
function SearchBar({ value, onChange }) {
  const isMobile = useContext(ThemeCtx)?.isMobile || false;
  if(isMobile) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "0 12px", gap: "8px", height: "36px", minWidth: "200px" }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Search products..."
        style={{ border: "none", outline: "none", fontSize: "13px", color: "#111827", background: "none", width: "160px" }}
      />
    </div>
  );
}

// ─── Spinner / LoadMore ───────────────────────
const Spinner = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", padding: "2rem", color: "#9CA3AF", fontSize: "14px" }}>
    <div style={{ width: "16px", height: "16px", border: "2px solid #E5E7EB", borderTopColor: "#6366F1", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    Loading...
    <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
  </div>
);

const LoadMore = ({ onClick }) => (
  <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
    <button onClick={onClick} style={{ padding: "9px 28px", borderRadius: "8px", border: "1px solid #E5E7EB", background: "#fff", color: "#6366F1", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
      Load more
    </button>
  </div>
);

// ─── Main screen ──────────────────────────────
// Single-line draggable filter row
function FilterRow({ label, children }) {
  const ref = useRef(null);
  const drag = useRef({ on:false, x:0, sx:0 });
  useEffect(()=>{
    const el=ref.current; if(!el) return;
    const down=e=>{ drag.current={on:true,x:e.clientX,sx:el.scrollLeft}; el.style.cursor="grabbing"; el.style.userSelect="none"; };
    const move=e=>{ if(!drag.current.on) return; el.scrollLeft=drag.current.sx-(e.clientX-drag.current.x); };
    const up=()=>{ drag.current.on=false; el.style.cursor="grab"; el.style.userSelect=""; };
    el.addEventListener("mousedown",down); el.addEventListener("mousemove",move);
    el.addEventListener("mouseup",up); el.addEventListener("mouseleave",up);
    return()=>{ el.removeEventListener("mousedown",down); el.removeEventListener("mousemove",move);
      el.removeEventListener("mouseup",up); el.removeEventListener("mouseleave",up); };
  },[]);
  return(
    <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"6px"}}>
      <span style={{fontSize:"10px",fontWeight:"700",color:"#9CA3AF",textTransform:"uppercase",
        letterSpacing:"0.6px",minWidth:"64px",flexShrink:0}}>{label}</span>
      <div ref={ref} style={{display:"flex",alignItems:"center",gap:"6px",
        overflowX:"auto",scrollbarWidth:"none",cursor:"grab",
        WebkitOverflowScrolling:"touch",paddingBottom:"2px"}}>
        <style>{"div::-webkit-scrollbar{display:none}"}</style>
        {children}
      </div>
    </div>
  );
}

export default function ProductProfileScreen({ user, subproject, defaultView="list", hideToggle=false }) {
  // All hooks at top — no early returns before this block
  const [view, setView]                       = useState(defaultView);
  const [filterPhase, setFilterPhase]         = useState(null);
  const [filterIndication, setFilterIndication] = useState(null);
  const [filterCountry, setFilterCountry]     = useState(null);
  const [editDoc,     setEditDoc]             = useState(null);
  const [editLoading, setEditLoading]         = useState(false);
  const [editErr,     setEditErr]             = useState("");
  const listData  = useProductLoader({ user, subproject });
  const chartData = useAllProductsLoader({ user, subproject });

  const effectiveView = hideToggle ? "chart" : view;
  const isAdmin = subproject?.ROLE === 1;

  // Paginated products for display
  const displayProducts = effectiveView === "chart" ? chartData.products : listData.products;
  const loading         = effectiveView === "chart" ? chartData.loading  : listData.loading;
  const error           = effectiveView === "chart" ? chartData.error    : listData.error;
  const searchInput     = listData.searchInput;
  const handleSearch    = listData.handleSearch;
  const loadMore        = listData.loadMore;

  // All products for accurate filter counts and filtered results
  const allProducts = chartData.products; // loaded in full via useAllProductsLoader

  const fetchAndEdit = async (docId, docTypeId) => {
    if(!docId) return;
    setEditLoading(true); setEditErr("");
    try {
      const r = await fetch(
        `${API_BASE}/advance_filter?userid=${user.USERID}&subprojectid=${subproject.SUBPROJECTID}&documentid=${docId}&doctypeid=${docTypeId}`,
        { headers:{"Auth-Code":user.AUTH_CODE} }
      );
      const d = await r.json();
      if((d.STATUS==="1"||d.STATUS===1)&&d.DATA?.length>0) setEditDoc(d.DATA[0]);
      else setEditErr("Could not load document for editing.");
    } catch { setEditErr("Network error."); }
    setEditLoading(false);
  };

  // Edit overlay — after hooks
  if(editDoc) return(
    <div style={{height:"100%",overflow:"auto",display:"flex",flexDirection:"column"}}>
      <div style={{padding:"10px 16px",borderBottom:"1px solid #E5E7EB",flexShrink:0,
        display:"flex",alignItems:"center",gap:"10px",background:"#fff"}}>
        <button onClick={()=>setEditDoc(null)}
          style={{padding:"5px 14px",borderRadius:"20px",border:"1px solid #E5E7EB",
            background:"#F9FAFB",color:"#6B7280",fontSize:"12px",fontWeight:"600",cursor:"pointer"}}>
          ← Back to Products
        </button>
        <span style={{fontSize:"14px",fontWeight:"700",color:"#111"}}>Edit Document</span>
      </div>
      <div style={{flex:1,overflow:"auto"}}>
        <CaptureContent session={{user,subproject}} editDoc={editDoc} onEditDone={()=>setEditDoc(null)}/>
      </div>
    </div>
  );

  if(editLoading) return(
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",gap:"10px",flexDirection:"column"}}>
      <div style={{width:"24px",height:"24px",border:"3px solid #E5E7EB",borderTopColor:"#6366F1",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
      <span style={{fontSize:"13px",color:"#6B7280"}}>Loading…</span>
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );

  if(editErr) return(
    <div style={{padding:"20px"}}>
      <div style={{color:"#EF4444",fontSize:"13px",marginBottom:"10px"}}>{editErr}</div>
      <button onClick={()=>setEditErr("")}
        style={{padding:"6px 14px",borderRadius:"8px",border:"1px solid #E5E7EB",
          background:"#F9FAFB",color:"#6B7280",cursor:"pointer",fontSize:"12px"}}>← Back</button>
    </div>
  );

  // Derive all unique phases, indications, countries from PRODUCT_DETAIL
  // Use allProducts for accurate counts; fall back to displayProducts while loading
  const countSource = allProducts.length > 0 ? allProducts : displayProducts;
  const allDetails = countSource.flatMap(p => Array.isArray(p.PRODUCT_DETAIL) ? p.PRODUCT_DETAIL : []);

  const phaseCounts = {};
  const indCounts   = {};
  const ctyCounts   = {};
  allDetails.forEach(d => {
    if (d.PHASENAME)     phaseCounts[d.PHASENAME]                        = (phaseCounts[d.PHASENAME]||0)+1;
    if (d.INDICATIONNAME) indCounts[d.INDICATIONNAME]                   = (indCounts[d.INDICATIONNAME]||0)+1;
    const cty = d.COUNTRYNAME;
    if (cty)             ctyCounts[cty]                                   = (ctyCounts[cty]||0)+1;
  });

  const phases      = PHASE_ORDER.filter(ph => phaseCounts[ph]);
  const indications = Object.entries(indCounts).sort((a,b)=>b[1]-a[1]).slice(0,8).map(([k])=>k);
  const countries   = Object.entries(ctyCounts).sort((a,b)=>b[1]-a[1]).slice(0,8).map(([k])=>k);

  // When filter active: search allProducts for complete results
  // When no filter: use paginated displayProducts
  const hasFilter = filterPhase || filterIndication || filterCountry;
  const hasMore   = effectiveView === "chart" ? false : (hasFilter ? false : listData.hasMore);
  const products  = hasFilter ? allProducts : displayProducts;
  const filtered = products.filter(p => {
    if (!filterPhase && !filterIndication && !filterCountry) return true;
    const dets = Array.isArray(p.PRODUCT_DETAIL) ? p.PRODUCT_DETAIL : [];
    return dets.some(d => {
      const cty = d.COUNTRYNAME;
      const phaseOk  = !filterPhase      || d.PHASENAME     === filterPhase;
      const indOk    = !filterIndication || d.INDICATIONNAME=== filterIndication;
      const ctyOk    = !filterCountry    || cty              === filterCountry;
      return phaseOk && indOk && ctyOk;
    });
  });

  const FilterChip = ({ label, value, active, onToggle, color }) => (
    <button onClick={() => onToggle(active ? null : value)}
      style={{ flexShrink:0, display:"flex", alignItems:"center", gap:"5px", padding:"4px 10px",
        borderRadius:"20px", border:`1.5px solid ${active ? (color||"#6366F1") : "#E5E7EB"}`,
        background: active ? (color ? color+"18" : "#EEF2FF") : "#fff",
        color: active ? (color||"#6366F1") : "#6B7280",
        fontSize:"11px", fontWeight: active?"700":"500",
        cursor:"pointer", transition:"all 0.15s", whiteSpace:"nowrap" }}>
      {label}
    </button>
  );

  return (
    <div style={{ padding: "20px", overflowY: "auto", height: "100%", boxSizing: "border-box" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "14px" }}>
        <div>
          <div style={{ fontSize: "19px", fontWeight: "600", color: "#111827" }}>Product Profiles</div>
          <div style={{ fontSize: "13px", color: "#9CA3AF", marginTop: "1px" }}>
            {subproject?.SUMMARY} · {filtered.length}{hasFilter ? ` of ${products.length}` : ""} products
          </div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
          {effectiveView === "list" && !hideToggle && <SearchBar value={searchInput} onChange={handleSearch} />}
          {!hideToggle && <ViewToggle view={effectiveView} onChange={setView} />}
        </div>
      </div>

      {error && <div style={{ padding: "10px 14px", borderRadius: "8px", fontSize: "13px", marginBottom: "1rem", background: "#FEF2F2", color: "#DC2626" }}>{error}</div>}

      {/* Chart view */}
      {effectiveView === "chart" && (loading && products.length === 0 ? <Spinner /> : <ChartView products={products} subId={subproject?.SUBPROJECTID}/>)}

      {/* List view */}
      {effectiveView === "list" && (
        <>
          {/* ── Filter bar ── */}
          <div style={{ background:"#F8FAFC", borderRadius:"12px", border:"1px solid #E5E7EB",
            padding:"10px 14px", marginBottom:"14px" }}>

            {/* Phase filters */}
            <FilterRow label="Phase">
              {phases.map(ph => {
                const cfg = PHASE_CONFIG[ph] || { color:"#6B7280", bg:"#F3F4F6" };
                return (
                  <FilterChip key={ph} label={`${ph} · ${phaseCounts[ph]}`}
                    value={ph} active={filterPhase===ph}
                    onToggle={setFilterPhase} color={cfg.color} />
                );
              })}
            </FilterRow>

            {/* Indication filters */}
            {indications.length > 0 && (
              <FilterRow label="Indication">
                {indications.map(ind => (
                  <FilterChip key={ind} label={`${ind.length>28?ind.slice(0,26)+"…":ind} · ${indCounts[ind]}`}
                    value={ind} active={filterIndication===ind}
                    onToggle={setFilterIndication} color="#6366F1" />
                ))}
              </FilterRow>
            )}

            {/* Country filters */}
            {countries.length > 0 && (
              <FilterRow label="Country">
                {countries.map(cty => (
                  <button key={cty} onClick={() => setFilterCountry(filterCountry===cty ? null : cty)}
                    style={{ flexShrink:0, display:"flex", alignItems:"center", gap:"5px", padding:"4px 10px",
                      borderRadius:"20px", border:`1.5px solid ${filterCountry===cty?"#0891B2":"#E5E7EB"}`,
                      background: filterCountry===cty ? "#ECFEFF" : "#fff",
                      color: filterCountry===cty ? "#0891B2" : "#6B7280",
                      fontSize:"11px", fontWeight: filterCountry===cty?"700":"500",
                      cursor:"pointer", transition:"all 0.15s", whiteSpace:"nowrap" }}>
                    <FlagIcon country={cty} size={13}/>
                    <span>{cty}{ctyCounts[cty]>0?` · ${ctyCounts[cty]}`:""}</span>
                  </button>
                ))}
              </FilterRow>
            )}

            {/* Clear filters */}
            {hasFilter && (
              <div style={{ marginTop:"10px", paddingTop:"8px", borderTop:"1px solid #E5E7EB", display:"flex", alignItems:"center", gap:"8px" }}>
                <span style={{ fontSize:"11px", color:"#6B7280" }}>
                  Filters active:
                  {filterPhase && <b style={{ color:"#1D4ED8" }}> {filterPhase}</b>}
                  {filterIndication && <b style={{ color:"#6366F1" }}> · {filterIndication.slice(0,30)}</b>}
                  {filterCountry && <b style={{ color:"#0891B2" }}> · {filterCountry}</b>}
                </span>
                <button onClick={() => { setFilterPhase(null); setFilterIndication(null); setFilterCountry(null); }}
                  style={{ marginLeft:"auto", padding:"3px 12px", borderRadius:"8px", border:"1px solid #E5E7EB",
                    background:"#fff", color:"#EF4444", fontSize:"11px", fontWeight:"600", cursor:"pointer" }}>
                  Clear all
                </button>
              </div>
            )}
          </div>

          {filtered.length === 0 && !loading && (
            <div style={{ textAlign: "center", padding: "4rem 0" }}>
              <div style={{ fontSize: "40px", marginBottom: "1rem", opacity: 0.2 }}>💊</div>
              <div style={{ fontSize: "15px", color: "#9CA3AF" }}>
                {hasFilter ? "No products match these filters" : "No products found"}
              </div>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "14px" }}>
            {filtered.map(p => (
              <ProductTile key={p.DOCUMENTID} product={p}
                onOpen={p=>openDocTab(p.DOCUMENTID, subproject?.SUBPROJECTID)}
onOpenDetail={d=>{ const base=window.location.origin+window.location.pathname; window.open(`${base}?doc=${d.DOCUMENTID}&dt=${d.DOCTYPEID||98}&sub=${subproject?.SUBPROJECTID||""}`, "_blank", "noopener,noreferrer"); }}
/>
            ))}
          </div>
          {loading && <Spinner />}
          {hasMore && !loading && products.length > 0 && <LoadMore onClick={loadMore} />}
        </>
      )}
    </div>

  );
}